import React, { useState } from "react";
import type { ConnectionWithUser } from "../../api/services/connections.service";
import { Badge, Button } from "../ui";
import { Avatar } from "../network/Avatar";
import { timeAgoLong } from "../network/timeAgo";

interface ConnectionRequestItemProps {
  connection: ConnectionWithUser;
  onAccept: (connectionId: string) => Promise<void>;
  onDecline: (connectionId: string) => Promise<void>;
}

const ConnectionRequestItem: React.FC<ConnectionRequestItemProps> = ({ connection, onAccept, onDecline }) => {
  const [action, setAction] = useState<"accept" | "decline" | null>(null);

  const { sourceUser, createdAt } = connection;
  const fullName = `${sourceUser?.firstName ?? ""} ${sourceUser?.lastName ?? ""}`.trim() || "FlipIQ member";
  const requestedAgo = timeAgoLong(createdAt);

  const run = async (kind: "accept" | "decline") => {
    setAction(kind);
    try {
      await (kind === "accept" ? onAccept(connection.id) : onDecline(connection.id));
    } finally {
      setAction(null);
    }
  };

  return (
    <div className="flex items-start gap-3 px-4 py-3.5">
      <Avatar src={sourceUser?.photoUrl} firstName={sourceUser?.firstName} lastName={sourceUser?.lastName} size="md" />

      <div className="min-w-0 flex-1">
        <div className="flex min-w-0 items-center gap-2">
          <p className="truncate text-sm font-semibold text-gray-900">{fullName}</p>
          {sourceUser?.role && (
            <Badge size="sm" className="capitalize">
              {sourceUser.role}
            </Badge>
          )}
        </div>
        <p className="mt-0.5 text-xs text-gray-500">
          Wants to connect{requestedAgo ? ` · ${requestedAgo}` : ""}
        </p>

        <div className="mt-2.5 flex items-center gap-2">
          <Button
            size="sm"
            variant="primary"
            onClick={() => run("accept")}
            loading={action === "accept"}
            disabled={action !== null}
            aria-label={`Accept connection request from ${fullName}`}
          >
            Accept
          </Button>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => run("decline")}
            loading={action === "decline"}
            disabled={action !== null}
            aria-label={`Decline connection request from ${fullName}`}
          >
            Decline
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ConnectionRequestItem;
